'use client';
import { Close, SettingsBackupRestore } from '@mui/icons-material';
import { Fade, Stack } from '@mui/material';
import { useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBreakpoint, useSearch } from '../../../../hooks';
import { isCompact } from '../../../../types';
import { IconButton } from '../../../buttons';
import { SearchBar } from '../../../searchbar/SearchBar';
import {
    EllipsedText,
    Item,
    SearchSectionLabel,
    StyledPaper,
    StyledStack,
    Suggestions
} from './styles';
import { Row } from '../../../../styles';
import { SuggestionType } from './Search';
import { splitAtFirstSpace } from '../../../../utils';
import { BoldedText } from '../../../bolded-text/BoldedText';
import { Company } from '../../../../types/Institution';
import { Project } from '../../../../types/Project';
import { useTranslations } from 'next-intl';

type SearchSuggestionsProps = {
    onMenuClick: () => void;
    goToDirectly: (route: SuggestionType, id: string) => void;
    clearMatches: () => void;
    fetchSuggestions: (term: string) => void;
    companyMatches: Company[];
    projectMatches: Project[];
};

const SearchSuggestions = ({
    onMenuClick,
    goToDirectly,
    clearMatches,
    fetchSuggestions,
    companyMatches,
    projectMatches
}: SearchSuggestionsProps) => {
    const breakpoint = useBreakpoint();
    const t = useTranslations('searchbar');
    const searchParams = useSearchParams();
    const searchTerm = searchParams.get('term');
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<null | number>(null);
    const { pastSearches, refreshPastSearches, removePastSearch, onSearch, updatePastSearches } =
        useSearch();
    const isCompactLayout = isCompact(breakpoint);

    const hasPastSearches = useMemo(
        () => pastSearches.length > 0 && value.trim().length === 0,
        [pastSearches, value]
    );

    const hasMatches = useMemo(
        () => value.trim().length > 0 && (projectMatches.length > 0 || companyMatches.length > 0),
        [value, projectMatches.length, companyMatches.length]
    );

    const reset = useCallback(() => {
        setActiveSuggestionIndex(null);
        setValue('');
        setIsFocused(false);
    }, []);

    useEffect(() => {
        if (isFocused) refreshPastSearches();
    }, [isFocused, refreshPastSearches]);

    const handleChange = ({ target: { value: val } }: React.ChangeEvent<HTMLInputElement>) => {
        setActiveSuggestionIndex(null);
        setValue(val);
        fetchSuggestions(val);
    };

    const handleSearch = useCallback(
        (term: string) => {
            if (term.trim().length < 1) return;
            onSearch(term);
            setActiveSuggestionIndex(null);
        },
        [onSearch]
    );

    const handleSelectPastSearch = useCallback(
        (term: string) => {
            setValue(term);
            handleSearch(term);
        },
        [handleSearch]
    );

    const handleSelectSuggestion = useCallback(
        (suggestionType: SuggestionType, id: string, term: string) => {
            goToDirectly(suggestionType, id);
            setValue(term);
            updatePastSearches(term);
            setActiveSuggestionIndex(null);
            clearMatches();
        },
        [clearMatches, goToDirectly, updatePastSearches]
    );

    const getSuggestionByIndex = useCallback(
        (idx: number) => {
            const matches = [...projectMatches, ...companyMatches];
            return matches[idx];
        },
        [projectMatches, companyMatches]
    );

    const handleSearchRemoval =
        (term: string) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            removePastSearch(term);
        };

    const getPastSearchByIndex = useCallback(
        (idx: number) => [...pastSearches].reverse()[idx],
        [pastSearches]
    );

    const handleEnter = () => {
        if (activeSuggestionIndex === null) handleSearch(value);
        else if (hasMatches) {
            const suggestion = getSuggestionByIndex(activeSuggestionIndex);
            const suggestionIsProject = (sug: Company | Project): sug is Project =>
                Object.keys(sug).includes('title');
            const isProject = suggestionIsProject(suggestion);
            handleSelectSuggestion(
                isProject ? SuggestionType.Project : SuggestionType.Company,
                suggestion.documentId,
                isProject ? suggestion.title : suggestion.name
            );
        } else if (hasPastSearches) {
            const suggestedTerm = getPastSearchByIndex(activeSuggestionIndex);
            suggestedTerm && handleSelectPastSearch(suggestedTerm);
        }
        setIsFocused(false);
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleEnter();
        } else if (e.key === 'ArrowDown') {
            setActiveSuggestionIndex(
                activeSuggestionIndex === null ? 0 : activeSuggestionIndex + 1
            );
        } else if (e.key === 'ArrowUp') {
            setActiveSuggestionIndex(
                activeSuggestionIndex === null ? null : activeSuggestionIndex - 1
            );
        }
    };

    const renderPastSearches = () => {
        return (
            <Stack>
                {pastSearches?.length > 0 && (
                    <>
                        <SearchSectionLabel>{t('last_searches')}</SearchSectionLabel>
                        {[...pastSearches].reverse().map((term: string, idx: number) => (
                            <Item
                                key={idx}
                                onClick={() => handleSelectPastSearch(term)}
                                hasPadding={false}
                                isActive={idx === activeSuggestionIndex}
                            >
                                <Row>
                                    <IconButton icon={<SettingsBackupRestore />} />
                                    <EllipsedText>{term}</EllipsedText>
                                </Row>

                                <IconButton icon={<Close />} onClick={handleSearchRemoval(term)} />
                            </Item>
                        ))}
                    </>
                )}
            </Stack>
        );
    };

    const renderMatchesPreview = useCallback(() => {
        const searchterms = splitAtFirstSpace(value.trim().toLowerCase());
        return (
            <Suggestions>
                {projectMatches.length > 0 ? (
                    <>
                        <SearchSectionLabel>Projekte</SearchSectionLabel>
                        {projectMatches.map((project, idx) => {
                            return (
                                <Item
                                    key={project.documentId}
                                    hasPadding
                                    onClick={() => {
                                        handleSelectSuggestion(
                                            SuggestionType.Project,
                                            project.documentId,
                                            project.title
                                        );
                                    }}
                                    isActive={idx === activeSuggestionIndex}
                                >
                                    <EllipsedText>
                                        <BoldedText
                                            fullString={project.title}
                                            inBold={searchterms}
                                        />
                                    </EllipsedText>
                                </Item>
                            );
                        })}
                    </>
                ) : null}
                {companyMatches.length > 0 ? (
                    <>
                        <SearchSectionLabel>Unternehmen</SearchSectionLabel>
                        {companyMatches.map((company, idx) => {
                            return (
                                <Item
                                    // we add the projectMatches length to treat users and projects as one list (for keyboard controls)
                                    key={idx + projectMatches.length}
                                    hasPadding
                                    onClick={(_) => {
                                        handleSelectSuggestion(
                                            SuggestionType.Company,
                                            company.documentId,
                                            company.name
                                        );
                                    }}
                                    isActive={idx + projectMatches.length === activeSuggestionIndex}
                                >
                                    <EllipsedText>
                                        <BoldedText
                                            fullString={company.name}
                                            inBold={searchterms}
                                        />
                                    </EllipsedText>
                                </Item>
                            );
                        })}
                    </>
                ) : null}
            </Suggestions>
        );
    }, [value, projectMatches, companyMatches, activeSuggestionIndex, handleSelectSuggestion]);

    useEffect(() => {
        setValue(searchTerm?.length ? searchTerm : '');
    }, [searchTerm]);

    return (
        <StyledStack>
            <SearchBar
                onMenuClick={onMenuClick}
                onChange={handleChange}
                onCancel={reset}
                onSearch={handleSearch}
                onKeyUp={handleKeyUp}
                onFocusChange={(focus) => setIsFocused(focus)}
                isCompact={isCompactLayout}
                isFocused={isFocused}
                value={value}
            />
            <Fade in={isFocused}>
                <StyledPaper
                    elevation={4}
                    isCompact={isCompactLayout}
                    data-testid='search-dropdown'
                >
                    {hasMatches ? renderMatchesPreview() : renderPastSearches()}
                </StyledPaper>
            </Fade>
        </StyledStack>
    );
};

export { SearchSuggestions };
export type { SearchSuggestionsProps };
